import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDate } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { FileClock, AlertTriangle } from "lucide-react";

const DOC_STATUS = {
  expiring_90: ["bg-blue-100 text-blue-700", "läuft ab ≤90 Tage"],
  expiring_30: ["bg-amber-100 text-amber-800", "läuft ab ≤30 Tage"],
  expired: ["bg-red-100 text-red-700", "abgelaufen"],
  missing: ["bg-gray-100 text-gray-600", "fehlt (Pflicht)"],
};

const TILES = [
  ["expired", "Abgelaufen", "text-red-600"],
  ["missing", "Fehlt (Pflicht)", "text-gray-600"],
  ["expiring_30", "≤30 Tage", "text-amber-600"],
  ["expiring_90", "≤90 Tage", "text-blue-600"],
];

export default function SupplierDocuments() {
  const navigate = useNavigate();
  const [data, setData] = useState({ rows: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    const params = filter ? { status: filter } : {};
    api.get("/srm/document-deadlines", { params }).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="supplier-documents-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-11 w-11 rounded-xl bg-[#1B2A4A] flex items-center justify-center">
          <FileClock className="h-6 w-6 text-[#F5A623]" strokeWidth={1.75} />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#1B2A4A]">Dokumenten-Fristen</h1>
          <p className="text-sm text-gray-500">Ablaufende & fehlende Pflichtnachweise aller Lieferanten</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {TILES.map(([k, label, color]) => (
          <button key={k} onClick={() => setFilter(filter === k ? "" : k)} data-testid={`deadline-tile-${k}`}
            className={`text-left bg-white rounded-xl border p-4 transition ${filter === k ? "border-[#1B2A4A] ring-1 ring-[#1B2A4A]" : "border-gray-200 hover:border-gray-300"}`}>
            <div className={`text-2xl font-bold ${color}`}>{data.totals?.[k] || 0}</div>
            <div className="text-xs text-gray-500 mt-1">{label}</div>
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Lieferant</TableHead><TableHead>Dokument</TableHead>
              <TableHead>Status</TableHead><TableHead>Gültig bis</TableHead><TableHead>Tage</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={5} className="text-center text-gray-400 py-8">Lädt…</TableCell></TableRow>}
            {!loading && (data.rows || []).length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-gray-400 py-8">
                <div className="flex flex-col items-center gap-2"><AlertTriangle className="h-5 w-5 text-gray-300" />Keine offenen Fristen</div>
              </TableCell></TableRow>
            )}
            {(data.rows || []).map((r, i) => {
              const [cls, label] = DOC_STATUS[r.status] || ["bg-gray-100", r.status];
              return (
                <TableRow key={`${r.account_id}-${r.document_type}-${i}`} data-testid={`deadline-row-${r.account_id}-${r.document_type}`}
                  className="cursor-pointer hover:bg-gray-50" onClick={() => navigate(`/suppliers/${r.account_id}`)}>
                  <TableCell className="font-medium text-[#1B2A4A]">{r.account_name}<span className="text-xs text-gray-400 font-mono ml-2">{r.account_number}</span></TableCell>
                  <TableCell>{r.label}</TableCell>
                  <TableCell><Badge className={cls}>{label}</Badge></TableCell>
                  <TableCell className="text-sm text-gray-600">{r.valid_until ? fmtDate(r.valid_until) : "–"}</TableCell>
                  <TableCell className={`text-sm font-medium ${r.days_to_expiry != null && r.days_to_expiry < 0 ? "text-red-600" : "text-gray-600"}`}>{r.days_to_expiry != null ? `${r.days_to_expiry}d` : "–"}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
